/** @jsx jsx */

import { ComponentProps, ReactNode, useEffect, useState } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import {
  Colors,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Accordion from '../Accordion';
import MenuItem from './MenuItem';

const Styles = {
  list: css`
    margin: 0 10px 0 20px;
  `,
  root: css`
    padding: 10px 0px;
    :not(:last-of-type) {
      border-bottom: 1px solid ${Colors.GrayWarm4};
    }
  `,
};

type Props = {
  badge?: ReactNode;
  disabledMessage?: ReactNode;
  hasTopBorder?: boolean;
  header?: ReactNode;
  itemsProps: Array<ComponentProps<typeof MenuItem>>;
  menuKey: string;
  title: string;
  titleIconName?: ComponentProps<typeof Icon>['name'];
};

const defaultProps = {
  hasTopBorder: false,
};

function Menu({
  badge,
  disabledMessage,
  hasTopBorder = defaultProps.hasTopBorder,
  header,
  itemsProps,
  menuKey,
  title,
  titleIconName,
}: Props) {
  const groupedItemProps = itemsProps.reduce((accum, itemProps, index, all) => {
    if (!index || itemProps.item.isEnabled !== all[index - 1].item.isEnabled) {
      accum.push([]);
    }
    accum[accum.length - 1].push(itemProps);
    return accum;
  }, []);

  // localStorage key for the accordion expanded state
  // TODO: This should be stored per-app, not globally for all apps.
  const storageKey = `app.sidebar.menu.${menuKey}.expandedState`;

  // The entire menu should be hidden while the state is `unknown`, because we
  // are async loading the app status to determine the initial state (`open` or
  // `closed`). That feature isn't implemented yet, though, so only open/closed
  // are used right now.
  type ExpandedState = 'open' | 'closed' | 'unknown';

  // Load expanded state from localStorage
  const [expandedState, setExpandedState] = useState<ExpandedState>(() => {
    try {
      const value: unknown = JSON.parse(localStorage.getItem(storageKey));
      switch (value) {
        case 'open':
        case 'closed':
        case 'unknown':
          return value;
        default:
          throw new Error(
            `invalid localStorage value (${value}) for key "${storageKey}"`
          );
      }
    } catch (err) {
      return 'open';
    }
  });

  // Sync expanded state changes to localStorage, unless it's not set yet
  useEffect(() => {
    if (expandedState === 'unknown') {
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(expandedState));
  }, [expandedState, storageKey]);

  // We can't render the accordion until we know whether it should be expanded
  // by default or not. This is blocked on async loading the `appStatus` so we
  // can find out if it's private or not.
  if (expandedState === 'unknown') {
    return null;
  }

  return (
    <div css={Styles.root}>
      <Accordion
        badge={badge}
        hasTopBorder={hasTopBorder}
        isExpandedByDefault={expandedState === 'open'}
        key={storageKey}
        onClose={() => setExpandedState('closed')}
        onExpand={() => setExpandedState('open')}
        title={title}
        titleIconName={titleIconName}
      >
        {header}
        <ul css={Styles.list}>
          {groupedItemProps.map((itemPropsArray, index) => {
            const menuItems = itemPropsArray.map(itemProps => (
              <MenuItem hasLeftIndent={true} {...itemProps} />
            ));

            const isGroupEnabled = itemPropsArray[0].item.isEnabled;
            return isGroupEnabled ? (
              menuItems
            ) : (
              <Tooltip
                allowMultiline={true}
                content={
                  typeof disabledMessage === 'string' ? disabledMessage : ''
                }
                key={index}
              >
                <TooltipFocusableTrigger tag="div">
                  {menuItems}
                </TooltipFocusableTrigger>
              </Tooltip>
            );
          })}
        </ul>
      </Accordion>
    </div>
  );
}

Menu.defaultProps = defaultProps;

export default Menu;
